/**
 * Cell value renderer for the Data Explorer grid.
 *
 * Renders a single cell value with type-aware formatting, truncation, and copy affordance.
 */

import React, { useState } from 'react';
import { useTheme, fontSize, fontFamily, Icon } from '@fti/design-system';
import { isHttpUrl, toEpochMs, formatDate, formatNumber, arrayPreview, objectPreview, copyToClipboard } from './format';
import type { CellValueProps } from './contracts';

export const CellValue = React.memo(function CellValue({ value, type }: CellValueProps) {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(value);
    if (success) {
      setShowCheck(true);
      setTimeout(() => setShowCheck(false), 1200);
    }
  };

  // Container for all cell content
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
  };

  const textStyle: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  };

  const mutedStyle: React.CSSProperties = {
    ...textStyle,
    fontStyle: 'italic',
    color: theme.colors.text.secondary,
  };

  const copyButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    background: theme.colors.background.paper,
    border: 'none',
    padding: '2px',
    cursor: 'pointer',
    display: isHovered ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
  };

  // Handle undefined (absent key)
  if (value === undefined) {
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={mutedStyle} title="absent">
          —
        </span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle null
  if (value === null) {
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={mutedStyle} title="null">
          —
        </span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle number
  if (typeof value === 'number') {
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ ...textStyle, textAlign: 'right' }}>{formatNumber(value)}</span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle boolean
  if (typeof value === 'boolean') {
    const color = value
      ? theme.colors.semantic.success.default
      : theme.colors.semantic.error.default;
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ ...textStyle, color }}>{String(value)}</span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle date column
  if (type === 'date') {
    const ms = toEpochMs(value);
    if (ms !== null) {
      return (
        <div
          style={containerStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span style={textStyle} title={String(value)}>
            {formatDate(ms)}
          </span>
          <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
            <Icon name={showCheck ? 'check' : 'copy'} size={12} />
          </button>
        </div>
      );
    }
  }

  // Handle URL strings
  if (isHttpUrl(value)) {
    const handleLinkClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleLinkClick}
          style={{
            ...textStyle,
            color: theme.colors.primary[600],
            textDecoration: 'none',
          }}
          title={value}
        >
          {value}
        </a>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle string
  if (typeof value === 'string') {
    const truncated = value.length > 500 ? value.slice(0, 500) : value;
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={textStyle} title={truncated}>
          {value}
        </span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle array
  if (Array.isArray(value)) {
    const preview = arrayPreview(value);
    const titleText = JSON.stringify(value).slice(0, 500);
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ ...textStyle, color: theme.colors.text.secondary }} title={titleText}>
          {preview}
        </span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Handle object
  if (typeof value === 'object') {
    const preview = objectPreview(value as Record<string, unknown>);
    const titleText = JSON.stringify(value).slice(0, 500);
    return (
      <div
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ ...textStyle, color: theme.colors.text.secondary }} title={titleText}>
          {preview}
        </span>
        <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
          <Icon name={showCheck ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    );
  }

  // Fallback for any other type
  return (
    <div
      style={containerStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span style={textStyle}>{String(value)}</span>
      <button style={copyButtonStyle} onClick={handleCopy} aria-label="Copy">
        <Icon name={showCheck ? 'check' : 'copy'} size={12} />
      </button>
    </div>
  );
});
